import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Closes a pop-up — a list, a calendar, a time picker — when the pointer goes down outside it or
 * Escape is pressed, for as long as it is open.
 *
 * Dropdown, DatePicker, TimePicker and DateRangeFilter each wrote these two listeners out for
 * themselves, the same way; this is the one copy. ProfileMenu and PublicHeader keep their own.
 *
 * An Escape pressed inside the pop-up is handled there first (escapeToTrigger): it closes the
 * pop-up and hands the focus back to the button that opened it, which unmounting the focused
 * control would otherwise drop on <body>. What reaches this listener is an Escape from outside
 * the pop-up, and a click outside it, where the focus stays where it is or where the pointer put it.
 *
 * `onDismiss` is read when the event arrives, not when the listeners were attached, so a caller
 * need not keep it stable — DatePicker's close is a fresh function on every render.
 */
export function useDismiss(ref: RefObject<HTMLElement>, open: boolean, onDismiss: () => void) {
  const latest = useRef(onDismiss);
  useLayoutEffect(() => {
    latest.current = onDismiss;
  });

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) latest.current();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') latest.current();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, ref]);
}
